import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Box,
  Flex,
  Grid,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCalendar, IconCurrencyDollar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { components, OptionProps } from 'react-select';

import { RootState } from '@/store';
import { useAppDispatch } from '@/store/hooks';
import { getAreFieldsEditable } from '@/store/selectors/transmissionReportSelectors';
import { useLinkAppointmentToTransmissionMutation } from '@/store/slices/api/appointmentApiSlice';
import {
  billingApiSlice,
  useUpdateBillingMutation,
  useUpdateIsBillableMutation
} from '@/store/slices/api/billingApiSlice';
import { useFeatureFlag } from '@/store/slices/api/featureFlagsApiSlice';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import {
  useGetTransmissionReportDetailsQuery,
  usePatchTransmissionReportMutation,
  useUpdateTransmissionTypeMutation
} from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { setEmrEncounterId } from '@/store/slices/form/transmissionReportFormSlice';
import { capitalize, getReportName, isReportInClinic } from '@/utils/constants';
import { API_SLICE } from '@/utils/constants/apiEndpoints';
import {
  PROFESSIONAL_OPTIONS_IN_CLINIC,
  PROFESSIONAL_OPTIONS_MANUAL,
  PROFESSIONAL_OPTIONS_REMOTE,
  TECHNICAL_OPTIONS,
  TECHNICAL_OPTIONS_HEART_FAILURE,
  TECHNICAL_OPTIONS_ILR,
  TECHNICAL_OPTIONS_IN_CLINIC
} from '@/utils/constants/billingCodes.tsx';
import formatDate from '@/utils/formatDate';
import { useNavigationReportIds } from '@/hooks/useNavigationReportIds';
import { useReportListDrawer } from '@/hooks/useReportListDrawer';
import { GrayAlertIcon } from '@/assets/icons/GrayAlertIcon';
import { GreenAlertIcon } from '@/assets/icons/GreenAlertIcon';
import { HeartAnalytics } from '@/assets/icons/HeartAnalytics';
import InfoIcon from '@/assets/icons/InfoIcon';
import { RedAlertIcon } from '@/assets/icons/RedAlertIcon';
import { YellowAlertIcon } from '@/assets/icons/YellowAlertIcon';

import { LabelAndValue } from '@/types/custom';
import { Appointment } from '@/types/matchDevice';
import {
  ReportCategoryType,
  ReportColor,
  ReportType,
  SignatureStatus
} from '@/types/transmissionReport';
import ConfirmationModal from '@/ui/ConfirmationModal';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';
import SkeletonGrid from '@/ui/Skeleton';
import SwitchTabs from '@/ui/SwitchTabs';
import TextInputWrapper from '@/ui/TextInputWrapper';

import { getReportColorForBadge } from '../utils/types';

import { AppointmentSelection } from './AppointmentSelection';
import styles from './ReportDetails.module.scss';
import { TransmissionSelection } from './TranmissionSelection';

export const reportTypeOptions: LabelAndValue[] = [
  { label: 'Alert Only', value: 'ALERT_ONLY' },
  { label: 'Combo (HF & Routine)', value: 'COMBO' },
  { label: 'Routine', value: 'ROUTINE' },
  { label: 'ERI Tracking', value: 'ERI_TRACKING' },
  { label: 'Heart Failure', value: 'HEART_FAILURE' },
  { label: 'Physician Requested', value: 'PHYSICIAN_REQUESTED' }
];

export const alertColorTypeOptions: LabelAndValue[] = [
  { label: 'Red', value: ReportColor.Red },
  { label: 'Yellow', value: ReportColor.Yellow },
  { label: 'No Alert', value: ReportColor.Green },
  { label: 'Untriaged', value: ReportColor.Gray }
];

type BadgeProps = {
  color: string;
  label: string;
};

const ReportDetails = () => {
  const { id } = useParams();
  const reportId = id ? parseInt(id) : 0;

  const [isUpdatingBillingDate, setIsUpdatingBillingDate] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAppointmentType, setPendingAppointmentType] = useState<string | null>(null);
  const [updateTransmissionType] = useUpdateTransmissionTypeMutation();
  const { isLoading, isError, data, refetch } = useGetTransmissionReportDetailsQuery(reportId);
  const areFieldsEditable = useSelector((state: RootState) => getAreFieldsEditable(state, false));
  const user = useSelector((state: RootState) => state.auth.user);
  const isInternalUser = user?.role?.includes('internal') ?? false;

  const report = data?.transmissionReport;

  const [encounterId, setEncounterId] = useState<string>(report?.emrEncounterId ?? '');

  const { data: clinicData } = useGetOrgByIdQuery(report?.patient?.clinic?.id ?? '', {
    skip: !report?.patient?.clinic?.id
  });

  const isAutoTriagedEnabled =
    clinicData?.organization?.isAutoTriageEnabled ??
    clinicData?.organization?.parent?.isAutoTriageEnabled ??
    false;

  const [isBillable, setIsBillable] = useState(report?.billing?.isBillable ?? false);
  const [alertColorType, setAlertColorType] = useState(
    report?.color === 'GREEN' || report?.color === 'GRAY' ? '' : report?.color
  );

  const { refetch: refetchNavigationReportIds } = useNavigationReportIds({
    reportId
  });

  const [updateBilling] = useUpdateBillingMutation();
  const isEditSchedulesEnabled = useFeatureFlag('edit_schedules');
  const isAutoTriageFeatureFlagEnabled = useFeatureFlag('triage_ui_visibility');

  const isAppointmentManagedInRs =
    report?.account?.isAppointmentManagedInRs ??
    report?.patient?.clinic?.isAppointmentManagedInRs ??
    false;

  const shouldShowEncounterId =
    report?.account?.enableEncounterId ?? report?.patient?.clinic?.enableEncounterId ?? false;

  const shouldDisplayBillingCodesDropdowns =
    clinicData?.organization?.parent?.displayBillingCodesDropdowns ??
    clinicData?.organization?.displayBillingCodesDropdowns ??
    false;

  const [updateIsBillable, { isLoading: isBillableUpdating }] = useUpdateIsBillableMutation();
  const [updateTransmissionReport] = usePatchTransmissionReportMutation();
  const [linkAppointmentToTransmission] = useLinkAppointmentToTransmissionMutation();

  const [professionalCodes, setProfessionalCodes] = useState<string[]>([]);
  const [technicalCode, setTechnicalCode] = useState<string | null>(null);
  const [reportType, setReportType] = useState<ReportCategoryType | null>(null);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [isAppointmentTypeLoading, setIsAppointmentTypeLoading] = useState(false);

  const { moveReport } = useReportListDrawer();
  const dispatch = useAppDispatch();

  const skipNextEffect = useRef(false);
  const lastReportId = useRef<number | null>(null);
  const billingId = report?.billing?.id ?? '';

  useEffect(() => {
    // Only run if report exists
    if (!report) return;

    const currentReportId = report.id;
    const isNewReport = lastReportId.current !== currentReportId;

    if (skipNextEffect.current && !isNewReport) {
      skipNextEffect.current = false;
      return;
    }
    // Update state for a new report or on load
    setProfessionalCodes(report?.billing?.professionalCodes?.map((code) => code.value) ?? []);
    setTechnicalCode(report?.billing?.technicalCode ?? null);
    setIsBillable(report?.billing?.isBillable ?? false);
    setAlertColorType(report?.color);
    setReportType(report?.reportType);
    setAppointment(report?.appointment ?? null);

    lastReportId.current = currentReportId;
  }, [report]);

  useEffect(() => {
    if (report?.emrEncounterId !== undefined) {
      setEncounterId(report.emrEncounterId ?? '');
    }
  }, [report?.emrEncounterId]);

  const BillingCodeOption = (props: OptionProps<any, true>) => {
    const { data } = props;
    const { isSelected } = props;
    const shortDescription = data.shortDescription ?? '';
    const description = data.description ?? '';
    return (
      <Box>
        <components.Option {...props}>
          <Group gap={0} align="center">
            <Text fw={400} fz="xs" mr={'3'}>
              {data.label}
            </Text>

            {description && (
              <Tooltip
                position="bottom-start"
                withArrow
                multiline
                w={280}
                label={description}
                withinPortal
                zIndex={9999}
                styles={{
                  tooltip: {
                    backgroundColor: '#4C0F28',
                    color: 'white',
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    lineHeight: 1.4
                  },
                  arrow: {
                    backgroundColor: '#4C0F28'
                  }
                }}>
                <span
                  style={{
                    display: 'flex',
                    cursor: 'pointer',
                  }}>
                  {isSelected ? <InfoIcon color="#fff" /> : <InfoIcon color="#4C0F28" />}
                </span>
              </Tooltip>
            )}
          </Group>

          <Text fz="xs" c="#9E8D94">
            {shortDescription}
          </Text>
        </components.Option>
      </Box>
    );
  };
  const handleReportTypeChange = async (option: LabelAndValue | null) => {
    if (!option) return;
    const newReportType = option.value as ReportCategoryType;
    setReportType(newReportType);
    try {
      await updateTransmissionReport({
        id: reportId,
        reportType: newReportType
      }).unwrap();
      const { data: updatedData } = await refetch();

      setProfessionalCodes(
        updatedData?.transmissionReport?.billing?.professionalCodes?.map((code) => code.value) ?? []
      );
      setTechnicalCode(updatedData?.transmissionReport?.billing?.technicalCode ?? null);

      dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));

      skipNextEffect.current = true;

      notifications.show({
        title: 'Success',
        message: 'Report Type Changed Successfully',
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error changing report type:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to change report type',
        color: 'red'
      });
    }
  };

  const handleAlertTypeChange = async (value: LabelAndValue | LabelAndValue[] | null) => {
    const selectedValue = Array.isArray(value) ? value[0]?.value : value?.value;
    setAlertColorType(selectedValue);

    const previousColor = report?.color as ReportColor;

    const newColor = selectedValue === '' ? ReportColor.Green : (selectedValue as ReportColor);
    try {
      await updateTransmissionReport({
        id: reportId,
        color: newColor
      }).unwrap();
      skipNextEffect.current = true;

      moveReport(reportId, previousColor, newColor);

      await refetchNavigationReportIds();

      notifications.show({
        title: 'Success',
        message: 'Alert Type Changed Successfully',
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error changing Alert type:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update Alert type',
        color: 'red'
      });
    }
  };

  const handleDateChange = async (date: Date | null) => {
    if (!date || !reportId || !billingId) return;

    try {
      setIsUpdatingBillingDate(true);
      const formattedDate = formatDate(date);

      await updateBilling({
        billingId,
        data: {
          transmissionReportId: reportId,
          billingDate: formattedDate
        }
      }).unwrap();
      await refetch();

      notifications.show({
        title: 'Success',
        message: 'Billing date updated successfully',
        color: 'green'
      });
    } catch (error) {
      console.error('Error updating billing date:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update billing date',
        color: 'red'
      });
    } finally {
      setIsUpdatingBillingDate(false);
    }
  };

  const handleIsBillableChange = async (e?: ChangeEvent<HTMLInputElement>) => {
    const isBillable = e?.target.checked ?? false;
    setIsBillable(isBillable);

    try {
      await updateIsBillable({
        billingId,
        data: {
          transmissionReportId: report?.id ?? 0,
          isBillable: isBillable
        }
      }).unwrap();

      const { data: updatedData } = await refetch();

      setProfessionalCodes(
        updatedData?.transmissionReport?.billing?.professionalCodes?.map((code) => code.value) ?? []
      );
      setTechnicalCode(updatedData?.transmissionReport?.billing?.technicalCode ?? null);

      skipNextEffect.current = true;

      notifications.show({
        title: 'Success',
        message: `Successfully made the report ${isBillable ? 'Billable' : 'Non billable'}`,
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      const message = `Error updating report to ${isBillable ? 'Billable' : 'Non billable'}`;
      console.error(message, error);
      notifications.show({
        title: 'Error',
        message,
        color: 'red'
      });
    }
  };

  const handleBillingCodesChange = async (
    name: 'professionalCodes' | 'technicalCode',
    value: LabelAndValue | LabelAndValue[] | null
  ) => {
    if (!reportId) return;

    let codes;
    if (name === 'professionalCodes') {
      // Handle multi-select for professional codes
      const selectedValues = Array.isArray(value)
        ? value.map((item) => item.value)
        : value
          ? [value.value]
          : [];
      codes = selectedValues.map((code) => ({ value: code }));
    } else {
      // Handle single select for technical code
      const selectedValue = Array.isArray(value) ? value[0]?.value : value?.value;
      codes = selectedValue ? { value: selectedValue } : null;
    }

    try {
      await updateBilling({
        billingId,
        data: {
          transmissionReportId: reportId,
          [name]: codes
        }
      }).unwrap();
      await refetch();

      skipNextEffect.current = true;

      notifications.show({
        title: 'Success',
        message: 'Billing codes updated successfully',
        color: 'green'
      });
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error updating billing codes:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update billing codes',
        color: 'red'
      });
    }
  };

  const handleAppointmentChange = async (option: LabelAndValue | null) => {
    const newAppointmentId = option?.value ?? null;
    setAppointment({ id: newAppointmentId } as Appointment);
    try {
      await linkAppointmentToTransmission({
        transmissionId: reportId,
        appointmentId: newAppointmentId
      }).unwrap();
      skipNextEffect.current = true;

      notifications.show({
        title: 'Success',
        message: newAppointmentId
          ? 'Appointment Linked Successfully to Transmission'
          : 'Appointment Unlinked Successfully from Transmission',
        color: 'green'
      });
    } catch (error: any) {
      skipNextEffect.current = false;
      console.error('Error linking/unlinking appointment to transmission:', error);

      if (error?.data?.details?.errorCode === 'APPOINTMENT_ALREADY_LINKED') {
        notifications.show({
          title: 'Failed to Link Appointment',
          message:
            error?.data?.message ??
            'This appointment already has a transmission associated with it',
          color: 'red'
        });
      } else {
        notifications.show({
          title: 'Error',
          message: newAppointmentId
            ? 'Failed to link appointment to transmission'
            : 'Failed to unlink appointment from transmission',
          color: 'red'
        });
      }
    }
  };

  const handleAppointmentTypeChange = async (id: string) => {
    const newTransmissionType = id === 'in-clinic' ? ReportType.InClinic : ReportType.Remote;
    if (
      report?.signatureStatus === SignatureStatus.Signed ||
      report?.signatureStatus === SignatureStatus.NonPractitionerSigned ||
      report?.signatures?.length
    ) {
      setPendingAppointmentType(null);
      setIsModalOpen(false);
      notifications.show({
        title: 'Warning',
        message: 'Report Type cannot be changed for Signed Reports',
        color: 'yellow'
      });
      return;
    }
    setIsAppointmentTypeLoading(true);
    try {
      await updateTransmissionType({
        id: reportId,
        transmissionType: newTransmissionType as ReportType
      }).unwrap();

      dispatch(billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }]));

      const { data: updatedData } = await refetch();

      setProfessionalCodes(
        updatedData?.transmissionReport?.billing?.professionalCodes?.map((code) => code.value) ?? []
      );
      setTechnicalCode(updatedData?.transmissionReport?.billing?.technicalCode ?? null);

      skipNextEffect.current = true;
      notifications.show({
        title: 'Success',
        message: 'Report Type Changed Successfully',
        color: 'green'
      });
      setPendingAppointmentType(null);
      setIsModalOpen(false);
    } catch (error) {
      skipNextEffect.current = false;
      console.error('Error changing report type:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to change report type',
        color: 'red'
      });
    } finally {
      setIsAppointmentTypeLoading(false);
    }
  };

  const handleEncounterIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEncounterId(value);
    dispatch(setEmrEncounterId(value));
  };

  const signatureBadgeMap: Partial<Record<SignatureStatus, BadgeProps>> = {
    [SignatureStatus.NotSigned]: { color: '#F59B00', label: 'DRAFT' },
    [SignatureStatus.SentToProvider]: { color: '#F59B00', label: 'DRAFT' },
    [SignatureStatus.NonPractitionerSigned]: { color: '#51C8FF', label: 'PENDING SIGNATURE' },
    [SignatureStatus.Signed]: { color: '#4FCC88', label: 'COMPLETE' },
    [SignatureStatus.PendingReview]: { color: '#FF644E', label: 'PENDING REVIEW' }
  };

  const memoizedData = useMemo(() => {
    const { transmissionReport } = data ?? {};
    if (!transmissionReport) return { professionalOptions: [], technicalOptions: [] };

    const {
      id: reportId,
      transmissionType,
      reportType,
      device: { deviceType } = { deviceType: null }
    } = transmissionReport;
    const isHeartFailure = reportType === ReportCategoryType.Heart_Failure;

    const isInClinic = isReportInClinic(data?.transmissionReport.transmissionType);
    const isRemote = transmissionType === ReportType.Remote;
    const isManual = transmissionType === ReportType.Manual;
    const isILR = deviceType === 'ILR';

    const fetchProfessionalOptions = () => {
      if (isRemote) return PROFESSIONAL_OPTIONS_REMOTE;
      else if (isInClinic) return PROFESSIONAL_OPTIONS_IN_CLINIC;
      else return PROFESSIONAL_OPTIONS_MANUAL;
    };

    const fetchTechnicalOptions = () => {
      if (isInClinic) return TECHNICAL_OPTIONS_IN_CLINIC;
      else if (isILR) return TECHNICAL_OPTIONS_ILR;
      else if (isHeartFailure) return TECHNICAL_OPTIONS_HEART_FAILURE;
      else if (isRemote || isManual) return TECHNICAL_OPTIONS;
      else return TECHNICAL_OPTIONS;
    };

    const convertToLabelValue = (options: any[]): LabelAndValue[] => {
      if (!Array.isArray(options)) return [];

      return options.map((option) => {
        // If already in correct format
        if (option && typeof option === 'object' && 'label' in option && 'value' in option) {
          return option as LabelAndValue;
        }
        // If it has different property names
        if (option && typeof option === 'object') {
          return {
            label: option.description || option.label || option.code || String(option),
            value: option.value || option.code || String(option)
          };
        }
        // If it's a string
        return { label: String(option), value: String(option) };
      });
    };

    return {
      professionalOptions: convertToLabelValue(fetchProfessionalOptions()),
      technicalOptions: convertToLabelValue(fetchTechnicalOptions()),
      reportId,
      transmissionType,
      isHeartFailure,
      deviceType,
      isInClinic,
      isRemote,
      isManual,
      isILR,
      reportType
    };
  }, [data]);

  if (isLoading) return <SkeletonGrid height={80} columns={2} rows={1} />;

  if (isError || !report) return <div>Error loading Patient Details</div>;

  const signatureBadge = signatureBadgeMap[report?.signatureStatus];

  const name = getReportName(
    report.device?.deviceType ?? '',
    report.transmissionType as ReportType,
    report.color,
    report?.reportType === ReportCategoryType.Heart_Failure
  );

  function handleAppointmentTypeTabClick(value: any): void {
    // If already selected, do nothing
    if (
      (memoizedData.isInClinic && value === 'in-clinic') ||
      (!memoizedData.isInClinic && value === 'remote')
    ) {
      return;
    }
    setPendingAppointmentType(value);
    setIsModalOpen(true);
  }

  function handleCancelAppointmentTypeChange() {
    setIsModalOpen(false);
    setPendingAppointmentType(null);
  }

  return (
    <div
      className={styles.root}
      style={!areFieldsEditable ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
      <div>
        <Grid>
          <Grid.Col span={{ lg: 5, xl: 6 }}>
            <Flex align={'center'} gap={12} wrap="wrap" mb={4}>
              <Title className={styles.title} fw={'500'}>
                {name}
              </Title>
              <div className={styles.alertIcon}>
                {report.color === ReportColor.Red && <RedAlertIcon />}
                {report.color === ReportColor.Yellow && <YellowAlertIcon />}
                {report.color === ReportColor.Green && <GreenAlertIcon />}
                {report.color === ReportColor.Gray && <GrayAlertIcon />}
              </div>
              {isInternalUser &&
                report?.transmission?.autoTriagedColor &&
                isAutoTriageFeatureFlagEnabled &&
                isAutoTriagedEnabled && (
                  <Tooltip
                    w={400}
                    multiline
                    label={
                      <>
                        <Stack gap={2}>
                          <Text fz={'sm'} fw={600}>
                            Synergy has triaged this report to{' '}
                            {capitalize(report?.transmission?.autoTriagedColor ?? '')}
                          </Text>
                        </Stack>
                        {report?.transmission?.protocolExplanation && (
                          <>
                            <Text fz={'xs'} fw={600} c="plum.3">
                              Alert Reason:
                            </Text>
                            <Text fz={'xs'} fw={500}>
                              {report?.transmission?.protocolExplanation || 'Auto-triaged'}
                            </Text>
                          </>
                        )}
                      </>
                    }
                    color="plum"
                    position="bottom-start"
                    withArrow>
                    <ActionIcon
                      size={24}
                      variant="filled"
                      radius="xl"
                      bg={getReportColorForBadge(
                        report?.transmission?.autoTriagedColor as ReportColor
                      )}
                      aria-label="Settings"
                      style={{
                        pointerEvents: 'auto'
                      }}>
                      <HeartAnalytics size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              {signatureBadge && (
                <Badge className={styles.signBadge} color={signatureBadge.color} variant="filled">
                  {signatureBadge.label}
                </Badge>
              )}
              {report?.billing?.isAppDeterminedBillable && (
                <Tooltip
                  label={
                    <Text fz={'xs'} fw={500}>
                      Synergy has determined this report to be billable.
                    </Text>
                  }
                  color="plum"
                  position="top-start"
                  withArrow>
                  <ActionIcon variant="filled" size="sm" bg="plum.3" c="plum">
                    <IconCurrencyDollar size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Flex>
          </Grid.Col>
          <Grid.Col span={2}>
            <LabelText label="APPOINTMENT TYPE" boxProps={{ mb: 2 }} />
            <SwitchTabs
              options={[
                { label: 'Remote', id: 'remote' },
                { label: 'In Clinic', id: 'in-clinic' }
              ]}
              value={memoizedData.isInClinic ? 'in-clinic' : 'remote'}
              onChange={handleAppointmentTypeTabClick}
            />
          </Grid.Col>
          <Grid.Col span={{ lg: 2.5, xl: 2 }}>
            <LabelText label="REPORT TYPE" boxProps={{ mb: 2 }} />
            <Dropdown
              isMulti={false}
              options={reportTypeOptions}
              value={reportTypeOptions.find((option) => option.value === reportType) || undefined}
              onChange={handleReportTypeChange}
              placeholder="Select Report Type"
            />
          </Grid.Col>

          <Grid.Col span={{ lg: 2.5, xl: 2 }}>
            <LabelText label="ALERT TYPE" boxProps={{ mb: 2 }} />
            <Dropdown
              isMulti={false}
              options={alertColorTypeOptions}
              value={
                alertColorTypeOptions.find((option) => option.value === alertColorType) || undefined
              }
              onChange={handleAlertTypeChange}
              placeholder="Select Alert Type"
            />
          </Grid.Col>
        </Grid>
        <Grid gutter={'lg'}>
          <Grid.Col span={{ lg: 2, xl: 1.5 }}>
            <LabelText label="Date of Service" boxProps={{ mb: 4 }} />
            <DatePickerInput
              placeholder="MM/DD/YYYY"
              required
              value={
                report.billing?.billingDate ? dayjs(report.billing.billingDate).toDate() : null
              }
              maxDate={new Date()}
              onChange={handleDateChange}
              rightSection={<IconCalendar size={18} />}
              disabled={isUpdatingBillingDate}
              clearable={false}
              valueFormat="MM/DD/YYYY"
            />
          </Grid.Col>
          <Grid.Col span={{ lg: 3, xl: 2 }}>
            <LabelText label="Transmission Selected" boxProps={{ mb: 2 }} />
            <TransmissionSelection reportId={reportId} patientId={report.patient.id} />
          </Grid.Col>
          {report?.matchDevice?.id && isEditSchedulesEnabled && isAppointmentManagedInRs && (
            <Grid.Col span={{ lg: 3, xl: 2 }}>
              <LabelText label="Appointment Selected" boxProps={{ mb: 2 }} />
              <AppointmentSelection
                reportId={reportId}
                deviceId={report.matchDevice?.id}
                appointment={appointment}
                handleChange={handleAppointmentChange}
                isDisabled={
                  report.signatureStatus === SignatureStatus.Signed ||
                  report.signatureStatus === SignatureStatus.NonPractitionerSigned
                }
              />
            </Grid.Col>
          )}

          <Grid.Col span={{ xl: report?.matchDevice?.id ? 0.5 : 2.5 }} visibleFrom="xl" />
          <Grid.Col span={2}>
            <Flex align={'center'} justify={'flex-start'} gap={8} h={'100%'} direction="row">
              {shouldShowEncounterId && (
                <TextInputWrapper
                  label="Encounter ID"
                  labelProps={{ variant: 'allCaps' }}
                  onChange={handleEncounterIdChange}
                  value={encounterId}
                />
              )}
              <Switch
                color="red"
                label="Billable"
                checked={isBillable}
                disabled={isBillableUpdating}
                onChange={handleIsBillableChange}
              />
              {isBillableUpdating && <Loader size={'xs'} />}
            </Flex>
          </Grid.Col>
          {((shouldDisplayBillingCodesDropdowns && memoizedData.isInClinic) || isBillable) && (
            <>
              <Grid.Col span={{ lg: 2.5, xl: 2 }}>
                <LabelText label="Professional Codes" boxProps={{ mb: 2 }} />
                <Dropdown
                  isMulti={true}
                  options={memoizedData.professionalOptions || []}
                  value={
                    professionalCodes
                      .map((code) =>
                        (memoizedData.professionalOptions || []).find((opt) => opt.value === code)
                      )
                      .filter(Boolean) as LabelAndValue[]
                  }
                  onChange={async (selected) => {
                    const selectedCodes = selected?.map((item) => item.value) ?? [];
                    setProfessionalCodes(selectedCodes);
                    await handleBillingCodesChange('professionalCodes', selected);
                  }}
                  placeholder={professionalCodes.length === 0 ? 'Select Codes' : ''}
                  isDisabled={isBillableUpdating}
                  customComponents={{ Option: BillingCodeOption }}
                />
              </Grid.Col>
              <Grid.Col span={{ lg: 2.5, xl: 2 }}>
                <LabelText label="Technical Code" boxProps={{ mb: 2 }} />
                <Dropdown
                  isMulti={false}
                  options={memoizedData.technicalOptions || []}
                  value={
                    (memoizedData.technicalOptions || []).find(
                      (opt) => opt.value === technicalCode
                    ) ?? { label: '-', value: '-' }
                  }
                  onChange={async (selected) => {
                    const selectedValue = Array.isArray(selected)
                      ? selected[0]?.value
                      : selected?.value;
                    setTechnicalCode(selectedValue || null);
                    await handleBillingCodesChange('technicalCode', selected);
                  }}
                  placeholder="Select Code"
                  isDisabled={isBillableUpdating}
                />
              </Grid.Col>
            </>
          )}
        </Grid>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelAppointmentTypeChange}
        title="Warning Modal"
        description={`Are you sure you want switch the report type from ${memoizedData.isInClinic ? 'In-Clinic' : 'Remote'} to ${pendingAppointmentType === 'in-clinic' ? 'In-Clinic' : 'Remote'}?`}
        onConfirm={() => {
          if (pendingAppointmentType) {
            handleAppointmentTypeChange(pendingAppointmentType);
          }
        }}
        isLoading={isAppointmentTypeLoading}
      />
    </div>
  );
};

export default ReportDetails;
